import type FlagType from 'owa-service/lib/contract/FlagType';
import { getUserConfiguration } from 'owa-session-store';
import { FlagCompleteEnum } from 'owa-mail-triage-action/lib/actions/helpers/getFlagCompleteState';
import type { OwaDate } from 'owa-datetime';
import {
    parse,
    now,
    isBefore,
    isSameDay,
    isSameWeek,
    isAfter,
    addWeeks,
    differenceInCalendarDays,
} from 'owa-datetime';
import type { ClientItemId } from 'owa-client-ids';
import { isCapabilityEnabled } from 'owa-capabilities';
import { advancedFlagCapability } from 'owa-capabilities-definitions/lib/advancedFlagCapability';
import { getCurrentSelectionFlagType } from './getCurrentSelectionFlagType';

/**
 * This function is used to help show selected state for the Flag/Unflag dropdown menu.
 * It determines this by calculating how far out the due date for the flag is relative to now,
 * and then returns a SelectedFlagState enum to determine which Flag/Unflag option should show selected state.
 */
export function getSelectedFlagState(itemId?: ClientItemId): FlagCompleteEnum {
    const selectedFlagType: FlagType | undefined | null = getCurrentSelectionFlagType(itemId);

    // Check for unflagged item
    if (selectedFlagType == undefined || selectedFlagType.FlagStatus === 'NotFlagged') {
        return FlagCompleteEnum.clear;
    }

    // Check for completed item
    if (selectedFlagType.FlagStatus === 'Complete') {
        return FlagCompleteEnum.complete;
    }

    // Check for flagged (with no date selected) item
    if (selectedFlagType.StartDate == undefined || selectedFlagType.DueDate == undefined) {
        if (selectedFlagType.FlagStatus === 'Flagged') {
            return FlagCompleteEnum.noDate;
        } else {
            return FlagCompleteEnum.clear;
        }
    }

    // Determine how far out the dueDate is in terms of FlagCompleteEnum
    const userOptions = getUserConfiguration()?.UserOptions;
    const userWeekStartDay = userOptions?.WeekStartDay;
    const userTimeZone = userOptions?.TimeZone;
    if (userOptions == undefined || userWeekStartDay == undefined || userTimeZone == undefined) {
        return FlagCompleteEnum.clear;
    }

    const currDate: OwaDate = now();
    const dueDate: OwaDate = parse(userTimeZone, selectedFlagType.DueDate);

    if (isSameDay(currDate, dueDate) || isBefore(dueDate, currDate)) {
        // If the due date has passed, we still want to show "Today"
        return FlagCompleteEnum.today;
    } else if (differenceInCalendarDays(dueDate, currDate) === 1) {
        return FlagCompleteEnum.tomorrow;
    } else if (isSameWeek(currDate, dueDate, userWeekStartDay)) {
        return FlagCompleteEnum.thisWeek;
    } else if (isSameWeek(addWeeks(currDate, 1), dueDate, userWeekStartDay)) {
        return FlagCompleteEnum.nextWeek;
    } else if (isCapabilityEnabled(advancedFlagCapability) && isAfter(dueDate, currDate)) {
        // If none of the above condition matches, and due date is in future, then return custom
        return FlagCompleteEnum.custom;
    } else {
        return FlagCompleteEnum.clear;
    }
}
